import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Phone, Mail, MapPin, Car, Calendar, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  quoted: "bg-blue-100 text-blue-800 border-blue-300",
  accepted: "bg-green-100 text-green-800 border-green-300",
  completed: "bg-slate-100 text-slate-800 border-slate-300",
  cancelled: "bg-red-100 text-red-800 border-red-300"
};

const serviceTypeLabels = {
  breakdown: "Roadside Breakdown & Recovery",
  auction: "Car Auction Transport",
  transport: "Vehicle Transportation",
  jumpstart: "Jump Start Service"
};

const carConditionLabels = {
  running: "Running / Drives",
  "non-running": "Non-Running / Needs Towing",
  rolls: "Rolls in Neutral"
};

export const AdminDashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotes`);
      setQuotes(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching quotes:", error);
      }
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/quotes/${quoteId}/status?status=${newStatus}`);
      toast.success("Status updated successfully");
      fetchQuotes(); // Refresh the list
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error updating status:", error);
      }
      toast.error("Failed to update status");
    }
  };

  const filteredQuotes = filter === "all" 
    ? quotes 
    : quotes.filter(q => q.status === filter);

  const stats = {
    total: quotes.length,
    pending: quotes.filter(q => q.status === "pending").length,
    quoted: quotes.filter(q => q.status === "quoted").length,
    accepted: quotes.filter(q => q.status === "accepted").length,
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600 mt-2">Manage quote requests for Ovawell Autos Ltd</p>
          </div>
          <Button onClick={fetchQuotes} disabled={loading} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Quotes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Quoted</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.quoted}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Accepted</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.accepted}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          <Button 
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
            size="sm"
          >
            All
          </Button>
          <Button 
            variant={filter === "pending" ? "default" : "outline"}
            onClick={() => setFilter("pending")}
            size="sm"
          >
            Pending
          </Button>
          <Button 
            variant={filter === "quoted" ? "default" : "outline"}
            onClick={() => setFilter("quoted")}
            size="sm"
          >
            Quoted
          </Button>
          <Button 
            variant={filter === "accepted" ? "default" : "outline"}
            onClick={() => setFilter("accepted")}
            size="sm"
          >
            Accepted
          </Button>
          <Button 
            variant={filter === "completed" ? "default" : "outline"}
            onClick={() => setFilter("completed")}
            size="sm"
          >
            Completed
          </Button>
        </div>

        {/* Quotes List */}
        {(() => {
          if (loading) {
            return (
              <div className="text-center py-12">
                <div className="inline-block w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full animate-spin"></div>
                <p className="mt-4 text-slate-600">Loading quotes...</p>
              </div>
            );
          }
          
          if (filteredQuotes.length === 0) {
            return (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-slate-600">No quotes found</p>
                </CardContent>
              </Card>
            );
          }
          
          return (
            <div className="space-y-6">
              {filteredQuotes.map((quote) => (
              <Card key={quote.id} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-slate-900">{quote.name}</h3>
                        <Badge className={`${statusColors[quote.status]} border`}>
                          {quote.status.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(quote.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <select 
                        value={quote.status}
                        onChange={(e) => updateQuoteStatus(quote.id, e.target.value)}
                        className="px-3 py-1 border border-slate-300 rounded-md text-sm"
                      >
                        <option value="pending">Pending</option>
                        <option value="quoted">Quoted</option>
                        <option value="accepted">Accepted</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Customer Info */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-slate-900 mb-3">Customer Information</h4>
                      <div className="flex items-center gap-2 text-slate-700">
                        <Phone className="w-4 h-4 text-slate-500" />
                        <a href={`tel:${quote.phone}`} className="hover:text-slate-900 hover:underline">
                          {quote.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700">
                        <Mail className="w-4 h-4 text-slate-500" />
                        <a href={`mailto:${quote.email}`} className="hover:text-slate-900 hover:underline">
                          {quote.email}
                        </a>
                      </div>
                    </div>

                    {/* Service Details */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-slate-900 mb-3">Service Details</h4>
                      <div className="text-slate-700">
                        <span className="font-medium">Service: </span>
                        {serviceTypeLabels[quote.serviceType]}
                      </div>
                      <div className="flex items-start gap-2 text-slate-700">
                        <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                        <div>
                          <div><span className="font-medium">From:</span> {quote.pickupPostcode}</div>
                          <div><span className="font-medium">To:</span> {quote.dropoffPostcode}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Details */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-3">Vehicle Information</h4>
                    <div className="flex items-center gap-6 text-slate-700">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-slate-500" />
                        <span><span className="font-medium">Vehicle:</span> {quote.carMake} {quote.carModel}</span>
                      </div>
                      <div>
                        <span className="font-medium">Condition:</span> {carConditionLabels[quote.carCondition]}
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  {quote.additionalInfo && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <h4 className="font-semibold text-slate-900 mb-2">Additional Information</h4>
                      <p className="text-slate-700 bg-slate-50 p-3 rounded-md">{quote.additionalInfo}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })()}
      </div>
    </div>
  );
};